import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const POINT_VALUES = {
  '#': 0,
  'No Roll': -1,
  '2': 36,
  '3': 18,
  '4': 12,
  '4H': 17,
  '5': 9,
  '6': 7,
  '6H': 12,
  '7': 6,
  '8': 7,
  '8H': 12,
  '9': 9,
  '10': 12,
  '10H': 17,
  '11': 18,
  '12': 36
};

const MAX_ROLLS = 50;

const DiceTracker = () => {
  const navigate = useNavigate();
  const [rolls, setRolls] = useState([]);
  const [savedSessions, setSavedSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [users, setUsers] = useState([]);
  const [userRole, setUserRole] = useState('user');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const controlsRef = useRef();

  useEffect(() => {
    initializeRolls();
    loadUsers();

    // Add scroll event listener for mini stats
    const handleScroll = () => {
      const fixedMiniStats = document.querySelector('.mini-stats[style*="position: fixed"]') ||
                            document.querySelectorAll('.mini-stats')[1]; // Fallback to second mini-stats div
      const statsPanel = document.querySelector('.stats-panel');
      if (fixedMiniStats && statsPanel) {
        const rect = statsPanel.getBoundingClientRect();
        if (rect.bottom < 50) {
          fixedMiniStats.style.display = 'block';
        } else {
          fixedMiniStats.style.display = 'none';
        }
      }
    };

    // Add keyboard event listener for roll shortcuts
    const handleKeyDown = (event) => {
      const key = event.key;
      let rollValue = null;

      if (key >= '2' && key <= '9') {
        rollValue = key;
      } else if (key === '0') {
        rollValue = '10';
      } else if (key === '1') {
        rollValue = '11';
      } else if (key === '-') {
        rollValue = 'No Roll';
      } else if (key === '=') {
        rollValue = '12';
      } else if (key === '$') {
        rollValue = '4H';
      } else if (key === '^') {
        rollValue = '6H';
      } else if (key === '*') {
        rollValue = '8H';
      } else if (key === ')') {
        rollValue = '10H';
      }

      if (rollValue) {
        event.preventDefault();
        addRoll(rollValue);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('keydown', handleKeyDown);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  const initializeRolls = () => {
    const initialRolls = Array(MAX_ROLLS).fill(null).map(() => ({
      roll: '',
      basePoints: 0,
      repeaterBonus: 0,
      totalPoints: 0,
      resetCount: 0,
      sevenOut: false,
      isReset: false
    }));
    setRolls(initialRolls);
    setHasScrolled(false);
  };

  const getBasePoints = (roll) => POINT_VALUES[roll] || 0;

  const getRepeaterBonus = (currentIndex, currentRoll, currentResetCount, rolls) => {
    if (currentRoll === '' || currentRoll === '7' || currentRoll === '#') return 0;
    let count = 0;
    for (let i = 0; i < currentIndex; i++) {
      if (rolls[i].roll === currentRoll && rolls[i].resetCount === currentResetCount) count++;
    }
    return count * 5;
  };

  const getNumberOfRolls = (rolls) => {
    const endIndex = rolls.findIndex(r => r.roll === '#');
    return endIndex >= 0 ? endIndex : rolls.filter(r => r.roll !== '').length;
  };

  const getCurrentDiceScore = (rolls) => {
    const numRolls = getNumberOfRolls(rolls);
    if (numRolls === 0) return 0;
    return rolls[numRolls - 1].totalPoints || 0;
  };

  const isNewRoll = (rolls) => {
    let rollsSinceReset = [];
    for (let i = rolls.length - 1; i >= 0; i--) {
      if (rolls[i].roll === '') continue;
      if (rolls[i].sevenOut) break;
      rollsSinceReset.unshift(rolls[i].roll);
    }
    for (let roll of rollsSinceReset) {
      if (roll === '4' || roll === '4H' || roll === '5' ||
          roll === '6' || roll === '6H' || roll === '8' ||
          roll === '8H' || roll === '9' || roll === '10' || roll === '10H') {
        return false;
      }
    }
    return true;
  };

  const getResetCount = (currentIndex, rolls) => {
    let count = 0;
    for (let i = 0; i < currentIndex; i++) {
      if (rolls[i].isReset) count++;
    }
    return count;
  };

  const addRoll = (value) => {
    setRolls(prevRolls => {
      const newRolls = [...prevRolls];
      const emptyIndex = newRolls.findIndex(r => r.roll === '');
      if (emptyIndex >= 0) {
        if (emptyIndex === 0 && !hasScrolled) {
          setHasScrolled(true);
          setTimeout(() => {
            controlsRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
        const resetCount = getResetCount(emptyIndex, newRolls);
        const basePoints = getBasePoints(value);
        const repeaterBonus = getRepeaterBonus(emptyIndex, value, resetCount, newRolls);
        const prevTotal = emptyIndex > 0 ? newRolls[emptyIndex - 1].totalPoints : 0;
        const totalPoints = prevTotal + basePoints + repeaterBonus;
        const isSevenOut = (value === '7' && !isNewRoll(newRolls));
        const isReset = isSevenOut || value === '#';

        newRolls[emptyIndex] = {
          roll: value,
          basePoints,
          repeaterBonus,
          totalPoints,
          resetCount: resetCount,
          sevenOut: isSevenOut,
          isReset: isReset
        };

        for (let i = emptyIndex + 1; i < newRolls.length; i++) {
          if (newRolls[i].roll === '') break;
          const soc = getResetCount(i, newRolls);
          const bp = getBasePoints(newRolls[i].roll);
          const rb = getRepeaterBonus(i, newRolls[i].roll, soc, newRolls);
          const pt = newRolls[i - 1].totalPoints + bp + rb;
          newRolls[i] = { ...newRolls[i], basePoints: bp, repeaterBonus: rb, totalPoints: pt, resetCount: soc };
        }
      }
      return newRolls;
    });
  };

  const undoLastRoll = () => {
    setRolls(prevRolls => {
      const newRolls = [...prevRolls];
      const lastIndex = newRolls.findIndex(r => r.roll === '');
      const indexToRemove = lastIndex > 0 ? lastIndex - 1 : newRolls.length - 1;
      if (indexToRemove >= 0 && newRolls[indexToRemove].roll !== '') {
        newRolls[indexToRemove] = { roll: '', basePoints: 0, repeaterBonus: 0, totalPoints: 0, resetCount: 0, sevenOut: false, isReset: false };
      }
      return newRolls;
    });
  };

  const clearAllRolls = () => {
    if (window.confirm('Are you sure you want to clear all rolls?')) {
      initializeRolls();
    }
  };

  const resetSession = () => {
    addRoll('#');
  };

  const saveSession = () => {
    const diceScore = rolls.reduce((sum, r) => sum + r.totalPoints, 0);
    const numRolls = rolls.filter(r => r.roll !== '').length;
    const rollsToSend = rolls.map(r => {
      return { ...r, roll: r.roll || '' };
    });
    const sessionData = {
      rolls: rollsToSend,
      diceScore,
      numRolls,
      resetRolls: rolls.filter(r => r.sevenOut).length,
      userId: selectedUserId || undefined
    };
    if (currentSessionId) {
      axios.put(`/api/sessions/dice/${currentSessionId}`, sessionData)
        .then(() => {
           alert('Session updated successfully');
           loadSessions();
         })
         .catch(error => {
           console.error('Error saving session:', (error.response && error.response.data) || error);
           alert('Error saving session: ' + ((error.response && error.response.data && error.response.data.error) || error.message));
         });
    } else {
      axios.post(`/api/sessions/dice`, sessionData)
        .then(response => {
          setCurrentSessionId(response.data._id);
           alert('Session saved successfully');
           loadSessions();
         })
         .catch(error => {
           console.error('Error saving session:', (error.response && error.response.data) || error);
           alert('Error saving session: ' + ((error.response && error.response.data && error.response.data.error) || error.message));
         });
    }
  };

  const loadSessions = async (userId = null) => {
    try {
      const targetUserId = userId || selectedUserId;
      console.log('Loading sessions for userId:', targetUserId);
      const url = targetUserId ? `/api/sessions/dice?userId=${targetUserId}` : `/api/sessions/dice`;
      console.log('API URL:', url);
      const response = await axios.get(url);
      setSavedSessions([]);
      setSavedSessions(response.data || []);
      console.log('Loaded sessions:', response.data);
    } catch (error) {
      console.error('Error loading sessions:', (error.response && error.response.data) || error);
      setSavedSessions([]);
      if (error.response && error.response.status === 401) {
        navigate('/login');
      }
    }
  };

  const loadUsers = async () => {
    let currentUserId = null;
    try {
      // Get current user info to determine role
      const storedUserData = localStorage.getItem('userData');
      if (storedUserData) {
        setIsLoggedIn(true);
        const userData = JSON.parse(storedUserData);
        currentUserId = userData.id;
        setUserRole(userData.role || 'user');
        setSelectedUserId(userData.id); // Default to current user

        // Load users if admin/manager
        if (userData.role === 'admin' || userData.role === 'manager') {
          const response = await axios.get(`/api/admin/users`);
          setUsers(response.data);
        }
      } else {
        setIsLoggedIn(false);
        setUserRole('user');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setIsLoggedIn(false);
    }
    // Load sessions after user setup is complete
    if (currentUserId) {
      loadSessions(currentUserId);
    }
  };

  const loadSession = (session) => {
    setRolls(session.rolls.map(r => ({
      roll: r.roll || '',
      basePoints: Number(r.basePoints) || 0,
      repeaterBonus: Number(r.repeaterBonus) || 0,
      totalPoints: Number(r.totalPoints) || 0,
      resetCount: Number(r.resetCount) || 0,
      sevenOut: Boolean(r.sevenOut),
      isReset: Boolean(r.isReset)
    })));
    setCurrentSessionId(session._id);
  };

  const newSession = () => {
    initializeRolls();
    setCurrentSessionId(null);
  };

  const diceScore = getCurrentDiceScore(rolls);
  const numRolls = getNumberOfRolls(rolls);
  const resetRolls = rolls.filter(r => r.sevenOut).length;

  return (
    <div style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", background: "#031A54", minHeight: "100vh", padding: "20px" }}>
      <div style={{ maxWidth: "1400px", margin: "0 auto", background: "white", borderRadius: "12px", boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)" }}>


        <div className="mini-stats" style={{ position: "fixed", top: "0", left: "50%", transform: "translateX(-50%)", width: "calc(100vw - 40px)", maxWidth: "1400px", background: "white", padding: "2px", display: "none", zIndex: "200", fontSize: "0.8rem", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)", borderRadius: "12px" }}>
          <div style={{ display: "inline-block", marginRight: "10px", padding: "2px 5px" }}>
            <div style={{ fontSize: "0.7rem", color: "#666" }}>Current Dice Score</div>
            <div style={{ fontSize: "0.9rem", fontWeight: "bold", background: "#4caf50", color: "white", padding: "5px 10px", borderRadius: "8px" }}>{diceScore}</div>
          </div>
          <div style={{ display: "inline-block", marginRight: "10px", padding: "2px 5px" }}>
            <div style={{ fontSize: "0.7rem", color: "#666" }}>Number of Rolls</div>
            <div style={{ fontSize: "0.9rem", fontWeight: "bold", color: "#1e3c72" }}>{numRolls}</div>
          </div>
          <div style={{ display: "inline-block", padding: "2px 5px" }}>
            <div style={{ fontSize: "0.7rem", color: "#666" }}>Reset Rolls</div>
            <div style={{ fontSize: "0.9rem", fontWeight: "bold", color: "#1e3c72" }}>{resetRolls}</div>
          </div>
        </div>

        <div className="stats-panel" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", padding: "20px", background: "#f5f5f5", borderBottom: "3px solid #9584AC" }}>
          <div style={{ background: "white", padding: "15px", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)", textAlign: "center" }}>
            <div style={{ fontSize: "0.85rem", color: "#666", marginBottom: "8px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>Current Dice Score</div>
            <div style={{ fontSize: "2rem", fontWeight: "bold", background: "#4caf50", color: "white", padding: "10px 15px", borderRadius: "12px" }}>{diceScore}</div>
          </div>
          <div style={{ background: "white", padding: "15px", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)", textAlign: "center" }}>
            <div style={{ fontSize: "0.85rem", color: "#666", marginBottom: "8px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>Number of Rolls</div>
            <div style={{ fontSize: "2rem", fontWeight: "bold", color: "#1e3c72" }}>{numRolls}</div>
          </div>
          <div style={{ background: "white", padding: "15px", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)", textAlign: "center" }}>
            <div style={{ fontSize: "0.85rem", color: "#666", marginBottom: "8px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>Reset Rolls</div>
            <div style={{ fontSize: "2rem", fontWeight: "bold", color: "#1e3c72" }}>{resetRolls}</div>
          </div>
        </div>

        <div ref={controlsRef} style={{ padding: "20px", background: "white", borderBottom: "2px solid #e0e0e0", position: "sticky", top: "40px", zIndex: "100" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(60px, 1fr))", gap: "8px", marginBottom: "20px" }}>
            {['2','3','4','4H','5','6','6H','7','8','8H','9','10','10H','11','12','No Roll'].map(val => (
              <button key={val} onClick={() => addRoll(val)} style={{
                padding: "10px", fontSize: "1rem", fontWeight: "bold", border: "2px solid #1e3c72", background: val.includes('H') || val === 'No Roll' ? "white" : "white", color: "#1e3c72", borderRadius: "8px", cursor: "pointer", transition: "all 0.2s", textTransform: "uppercase"
              }} onMouseOver={(e) => { e.target.style.background = '#1e3c72'; e.target.style.color = 'white'; }} onMouseOut={(e) => { e.target.style.background = 'white'; e.target.style.color = '#1e3c72'; }}>
                {val}
              </button>
            ))}
          </div>
        </div>

        <div style={{ padding: "20px", overflowX: "auto", maxHeight: "600px", overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", background: "white" }}>
            <thead>
              <tr>
                <th style={{ background: "#1e3c72", color: "white", padding: "12px", textAlign: "center", fontWeight: "600", textTransform: "uppercase", fontSize: "0.9rem", letterSpacing: "0.5px", position: "sticky", top: "0", zIndex: "10" }}>#</th>
                <th style={{ background: "#1e3c72", color: "white", padding: "12px", textAlign: "center", fontWeight: "600", textTransform: "uppercase", fontSize: "0.9rem", letterSpacing: "0.5px", position: "sticky", top: "0", zIndex: "10" }}>ROLL</th>
                <th style={{ background: "#1e3c72", color: "white", padding: "12px", textAlign: "center", fontWeight: "600", textTransform: "uppercase", fontSize: "0.9rem", letterSpacing: "0.5px", position: "sticky", top: "0", zIndex: "10" }}>BASE POINTS</th>
                <th style={{ background: "#1e3c72", color: "white", padding: "12px", textAlign: "center", fontWeight: "600", textTransform: "uppercase", fontSize: "0.9rem", letterSpacing: "0.5px", position: "sticky", top: "0", zIndex: "10" }}>REPEATER BONUS</th>
                <th style={{ background: "#1e3c72", color: "white", padding: "12px", textAlign: "center", fontWeight: "600", textTransform: "uppercase", fontSize: "0.9rem", letterSpacing: "0.5px", position: "sticky", top: "0", zIndex: "10" }}>TOTAL POINTS</th>
                <th style={{ background: "#1e3c72", color: "white", padding: "12px", textAlign: "center", fontWeight: "600", textTransform: "uppercase", fontSize: "0.9rem", letterSpacing: "0.5px", position: "sticky", top: "0", zIndex: "10" }}>RESET ROLLS</th>
              </tr>
            </thead>
            <tbody>
              {rolls.map((rollData, index) => (
                <tr key={index} style={{ cursor: "pointer" }}>
                  <td style={{ padding: "10px", textAlign: "center", background: "#f5f5f5", fontWeight: "bold", color: "#666" }}>{index + 1}</td>
                  <td style={{ padding: "10px", textAlign: "center", fontWeight: "bold", color: rollData.roll ? "#d32f2f" : "#ccc", fontSize: "1.1rem" }}>{rollData.roll || '—'}</td>
                  <td style={{ padding: "10px", textAlign: "center", color: rollData.roll ? "#1e3c72" : "#ccc", fontWeight: "600" }}>{rollData.roll ? rollData.basePoints : '—'}</td>
                  <td style={{ padding: "10px", textAlign: "center", color: rollData.roll ? "#1e3c72" : "#ccc", fontWeight: "600" }}>{rollData.roll ? rollData.repeaterBonus : '—'}</td>
                  <td style={{ padding: "10px", textAlign: "center", color: rollData.roll ? "#2e7d32" : "#ccc", fontWeight: "bold" }}>{rollData.roll ? rollData.totalPoints : '—'}</td>
                  <td style={{ padding: "10px", textAlign: "center", color: "#1e3c72", fontWeight: "600" }}>{rollData.roll ? rollData.resetCount : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", padding: "20px" }}>
          <button onClick={undoLastRoll} style={{ padding: "12px 24px", fontSize: "1rem", fontWeight: "600", border: "none", borderRadius: "12px", cursor: "pointer", background: "#ffa726", color: "white", transition: "all 0.2s" }}>↶ Undo Last</button>
          <button onClick={resetSession} style={{ padding: "12px 24px", fontSize: "1rem", fontWeight: "600", border: "none", borderRadius: "12px", cursor: "pointer", background: "#757575", color: "white", transition: "all 0.2s" }}># Reset Session</button>
          <button onClick={clearAllRolls} style={{ padding: "12px 24px", fontSize: "1rem", fontWeight: "600", border: "none", borderRadius: "12px", cursor: "pointer", background: "#d32f2f", color: "white", transition: "all 0.2s" }}>✖ Clear All</button>
        </div>

        {isLoggedIn && (
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", padding: "0 20px 20px 20px" }}>
            {(userRole === 'admin' || userRole === 'manager') && (
              <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                <label style={{ fontSize: "0.8rem", fontWeight: "600", color: "#333" }}>
                  User ({userRole}):
                </label>
                <select
                  value={selectedUserId}
                  onChange={(e) => {
                    setSelectedUserId(e.target.value);
                    loadSessions(e.target.value);
                  }}
                  style={{ padding: "8px 12px", fontSize: "0.9rem", border: "2px solid #e0e0e0", borderRadius: "8px", minWidth: "200px" }}
                >
                  {users.map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.firstName} {user.lastName} ({user.nickname})
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button onClick={saveSession} style={{ padding: "12px 24px", fontSize: "1rem", fontWeight: "600", border: "none", borderRadius: "12px", cursor: "pointer", background: "#1976d2", color: "white", transition: "all 0.2s" }}>💾 Save Session</button>
            <button onClick={newSession} style={{ padding: "12px 24px", fontSize: "1rem", fontWeight: "600", border: "none", borderRadius: "12px", cursor: "pointer", background: "#757575", color: "white", transition: "all 0.2s" }}>🆕 New Session</button>
            <select key={savedSessions.length} onChange={(e) => {
              if (e.target.value) {
                const session = savedSessions.find(s => s._id === e.target.value);
                if (session) loadSession(session);
              }
              e.target.value = '';
            }} style={{ padding: "12px 24px", fontSize: "1rem", fontWeight: "600", border: "none", borderRadius: "12px", cursor: "pointer", background: "#f5f5f5" }}>
              <option value="">📂 Load Session</option>
              {savedSessions.map(session => (
                <option key={session._id} value={session._id}>
                  {new Date(session.createdAt).toLocaleString()} - Score: {session.diceScore}
                </option>
              ))}
            </select>
          </div>
        )}
        {!isLoggedIn && (
          <div style={{ padding: "0 20px 20px 20px", textAlign: "center", color: "#666", fontStyle: "italic" }}>
            <p>🔒 Login required to save and load sessions</p>
          </div>
        )}

        <div style={{ padding: "15px", textAlign: "center", background: "#f5f5f5", color: "#666", fontSize: "0.85rem" }}>
          <p>Craps Players League ©2025</p>
        </div>
      </div>
    </div>
  );
};

export default DiceTracker;
